import { Suspense, lazy, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LoaderCircle, Smile } from "lucide-react";

// Code-split: emoji-picker-react bundles its full emoji dataset, so the chunk
// is only loaded from disk the first time the picker opens.
const EmojiPickerLazy = lazy(() => import("emoji-picker-react"));

const PICKER_WIDTH = 332;
const PICKER_HEIGHT = 400;

/**
 * Composer toolbar emoji picker: Smile trigger + popover-hosted searchable
 * picker (native glyphs — no network fetches). Emits the picked emoji as a
 * plain string; caret handling stays in the composer.
 *
 * @param {object} props
 * @param {(emoji: string) => void} props.onPickEmoji
 * @param {(event: Event) => void} [props.onCloseAutoFocus] — forwarded to the
 *   popover so the composer can return focus to the textarea on close
 */
export function ComposerEmojiPicker({ onPickEmoji, onCloseAutoFocus }) {
  const [open, setOpen] = useState(false);
  // Read while open is fine: the titlebar theme toggle can't be clicked
  // without dismissing the popover first.
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          aria-label="Insert emoji"
        >
          <Smile className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={6}
        className="w-auto overflow-hidden p-0"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <Suspense
          fallback={
            <div
              className="flex items-center justify-center"
              style={{ width: PICKER_WIDTH, height: PICKER_HEIGHT }}
            >
              <LoaderCircle className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
            </div>
          }
        >
          <EmojiPickerLazy
            className="epr-app-scroll"
            theme={isDark ? "dark" : "light"}
            emojiStyle="native"
            width={PICKER_WIDTH}
            height={PICKER_HEIGHT}
            previewConfig={{ showPreview: false }}
            lazyLoadEmojis
            onEmojiClick={(emojiData) => {
              setOpen(false);
              onPickEmoji?.(emojiData.emoji);
            }}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
}
